/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Box from "components/generals/Box";
import Typography from "components/generals/Typography";
import Accordion from "components/generals/Accordion";
import FormInput from "../generals/Input";
import {
  AppointmentInterface,
  HeaderEnum,
  PersonInterface,
  ProgressNoteInterface,
  TemplateInterface,
  VisitStatusEnum,
  PrescriptionTypeEnum,
  QueryResultInterface,
  BillingEncounterInterface,
  TransactionStatusEnum,
  AccountingInterface,
  AccountingMovementInterface
} from "@malmur-technologies/medicalsystem-shared";
import { FC, useEffect, useRef, useState } from "react";
import useHttp from "hooks/http.hook";
import { CoreAPI_URLs } from "constants/http.const";
import { getNewAppointment, getNewProgressNoteDataContent } from "utils/model.utils";
import { useTranslation } from "react-i18next";
import HPITemplatesDataContent from "components/content/HPITemplateDataContent";
import FormTextEditor from "components/generals/FormTextEditor";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { Method } from "axios";
import { EntityPermissions, FormActionType, PermissionsUser } from "types";
import VitalsDataContent from "components/content/VitalsDataContent";
import HeaderPersonInformation from "components/views/HeaderPersonInformation";
import ButtonDialog from "components/generals/ButtonDialog";
import Grid from "components/generals/Grid";
import IconButton from "components/generals/IconButton";
import AccordionDetails from "components/generals/AccordionDetails";
import AccordionSummary from "components/generals/AccordionSummary";
import Dialog from "components/generals/Dialog";
import DialogActions from "components/generals/DialogActions";
import DialogContent from "components/generals/DialogContent";
import DialogContentText from "components/generals/DialogContentText";
import DialogTitle from "components/generals/DialogTitle";
import Stack from "components/generals/Stack";
import UnfoldLessDoubleIcon from "@mui/icons-material/UnfoldLessDouble";
import HistoryIcon from "@mui/icons-material/History";
import Button from "components/generals/Button";
import HistoryProgressNoteCard from "components/views/HistoryProgressNoteCard";
import PrescriptionTemplate from "components/views/PrescriptionTemplate";
import PermissionWrapper from "components/generals/PermissionWrapper";
import PrescriptionTemplatesDataContent from "components/content/PrescriptionTemplateDataContent";
import AutocompleteCheckboxes from "components/generals/AutocompleteCheckboxes";
import { TagsInterface } from "interfaces";
import { useSelector } from "react-redux";
import { selectIsPatient, selectIsHeadNurse, selectIsProvider, selectHasRootPermissions } from "store/slices/auth.slice";
import ReceiptIcon from "@mui/icons-material/Receipt";
import AppointmentBillingEncountersForm from "./AppointmentBillingEncountersForm";
import { useLocation } from "react-router-dom";
import BillingEncounterTable from "components/tables/BillingEncounterTable";
import { InsuranceTransactionInterface } from "@malmur-technologies/medicalsystem-shared";
import PrescriptionHistoryModal from "components/views/PrescriptionHistoryModal";

/**
 * Props for ProgressNoteForm component.
 */
interface ProgressNoteFormProps {
  appointmentId?: string;
  historyActive?: boolean;
}

/**
 * Interface for color-coded options in progress note UI.
 */
export interface OptionColor {
  id: string;
  color: string;
}
//TODO: REMOVE MOCK DATA  L:100  HPI TITLE L:75 to be implemented

/**
 * Comprehensive clinical progress note form following SOAP format.
 * 
 * Features:
 * - SOAP documentation (Subjective, Objective, Assessment, Plan):
 *   - Chief complaints
 *   - History of Present Illness (HPI)
 *   - Physical examination
 *   - Diagnosis with ICD-10 codes
 *   - Treatment plan
 * - Integrated vitals recording and display
 * - Patient demographic header
 * - Template integration:
 *   - HPI templates with quick insertion
 *   - Prescription templates
 * - Rich text editor for detailed notes
 * - Medication orders and prescriptions
 * - Lab and imaging orders with notes
 * - Billing encounter creation and management
 * - Historical progress notes viewing
 * - Expandable/collapsible accordion sections
 * - Tag-based organization with color coding
 * - Auto-save on status change (check-out)
 * - Appointment status management
 * - Print functionality
 * - Permission-based field visibility
 * 
 * Clinical Workflow:
 * 1. Patient demographics displayed in header
 * 2. Vital signs recorded/displayed
 * 3. Chief complaints documented
 * 4. HPI selected from templates or custom entry
 * 5. Physical exam documented
 * 6. Diagnosis entered with ICD codes
 * 7. Treatment plan (medications, labs, imaging)
 * 8. Billing encounters created for services
 * 9. Note finalized and saved
 * 10. Appointment status updated to check-out
 * 
 * @param appointmentId - Associated appointment ID for clinical context
 * @param historyActive - Flag to show historical notes panel
 * @returns SOAP-format progress note form with clinical tools
 */
const ProgressNoteForm: FC<ProgressNoteFormProps> = ({ appointmentId, historyActive }) => {
  const [_isLoading, _error, sendRequest] = useHttp();
  const [personModel, setPersonModel] = useState<Partial<PersonInterface>>();
  const [appointmentModel, setAppointmentModel] =
    useState<Partial<AppointmentInterface>>(() => getNewAppointment(undefined, 15));
  const [progressNoteModel, setProgressNoteModel] = useState<Partial<ProgressNoteInterface>>(
    getNewProgressNoteDataContent()
  );
  const [isBillingModalOpen, setIsBillingModalOpen] = useState<boolean>(false);
  const [saveVitals, setSaveVitals] = useState<boolean>(false);
  const saveVitalsRef = useRef<boolean>(false);
  const [chiefComplaints, setChiefComplaints] = useState<string>("");
  const [diagnosis, setDiagnosis] = useState<string>("");
  const [notesMedicine, setNotesMedicine] = useState<string>("");
  const [notesLab, setNotesLab] = useState<string>("");
  const [notesImaging, setNotesImaging] = useState<string>("");
  const [comments, setComments] = useState<string>("");
  const [hpiText, setHpiText] = useState<string>();
  const [addHpi, setAddHpi] = useState<boolean>(false);
  const { t } = useTranslation();
  const [action, setAction] = useState<FormActionType>(FormActionType.NEW);
  const [hpiTemplateInfo, setHpiTemplateInfo] = useState<Partial<TemplateInterface>>();
  const [readOnlyForm, setReadOnlyForm] = useState<boolean>(true);
  const [readOnlyPrescriptions, setReadOnlyPrescriptions] = useState<boolean>(false);
  const [preview, setPreview] = useState<boolean>(true);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [openPrescriptionVersionModal, setOpenPrescriptionVersionModal] = useState<boolean>(false);
  const [openPrescriptionHistoryModal, setOpenPrescriptionHistoryModal] = useState<boolean>(false);
  const [reasonForChange, setReasonForChange] = useState<string>("");
  const [sectionsToPrint, setSectionsToPrint] = useState<string[]>([]);
  const [openPrescriptionModal, setOpenPrescriptionModal] = useState<boolean>(false);
  const [medicationText, setMedicationText] = useState<string>("");
  const [labText, setLabText] = useState<string>("");
  const [imagingText, setImagingText] = useState<string>("");
  const [addMedication, setAddMedication] = useState<boolean>(false);
  const [addLab, setAddLab] = useState<boolean>(false);
  const [addImaging, setAddImaging] = useState<boolean>(false);
  // Original values for comparison
  const [originalMedicationText, setOriginalMedicationText] = useState<string>("");
  const [originalLabText, setOriginalLabText] = useState<string>("");
  const [originalImagingText, setOriginalImagingText] = useState<string>("");
  const [optionColors, setOptionColors] = useState<OptionColor[]>([]);
  const [FilterTagListResponse, setFilterTagListResponse] = useState<TagsInterface[]>([]);
  const [selectedFilerTag, setSelectedFilerTag] = useState<TagsInterface[]>([]);
  const location = useLocation();
  const appointmentIdFromState = location.state?.data;
  const [billingEncounter, setBillingEncounter] = useState<BillingEncounterInterface[]>([]);
  const [billingFilter, setBillingFilter] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pageTotal, setPageTotal] = useState(1);
  const [rowsTotal, setRowsTotal] = useState(0);
  const isPatient = useSelector(selectIsPatient);
  const isHeadNurse = useSelector(selectIsHeadNurse);
  const isProvider = useSelector(selectIsProvider);
  const isRoot = useSelector(selectHasRootPermissions);
  const [error, setError] = useState("");
  const [showTimeWarningModal, setShowTimeWarningModal] = useState<boolean>(false);
  const [timeExtension, setTimeExtension] = useState<number>(0);
  appointmentId = appointmentIdFromState ?? appointmentId;

  const selectPrescriptionTemplate =
    (
      setText: React.Dispatch<React.SetStateAction<string>>,
      setFlag: React.Dispatch<React.SetStateAction<boolean>>
    ) =>
      (selectedPrescription: TemplateInterface) => {
        setText((prev) =>
          prev ? prev + selectedPrescription.template + "\n" : selectedPrescription.template + "\n"
        );
        setFlag(true);
      };
  useEffect(() => {
    if (appointmentId) {
      searchAppointment(appointmentId);
      searchBillings();
    }
  }, [appointmentId]);

  useEffect(() => { searchBillings(); }, [billingFilter, page, pageSize]);

  const searchBillings = () => {
    if (appointmentId || appointmentIdFromState) {
      const appointment_id = appointmentId ?? appointmentIdFromState;
      const nameParam = billingFilter ? `cpt=${billingFilter}&` : "";
      const url = `${CoreAPI_URLs.BILLING_ENCOUNTER_GET_ALL}?${nameParam}page=${page}&pageSize=${pageSize}`;
      sendRequest(url, onGetBillingEncounterHandler, "GET", null, [
        { header: HeaderEnum.APPOINTMENT_ID, value: appointment_id }
      ]);
    }
    else {
      setBillingEncounter(undefined);
      setRowsTotal(undefined);
      setPageTotal(Math.ceil(undefined));
    }
  };

  const onGetBillingEncounterHandler = (response: QueryResultInterface<BillingEncounterInterface>) => {
    setBillingEncounter(response.results);
    setRowsTotal(response.total);
    setPageTotal(Math.ceil(response.total / pageSize));
  };

  const searchAppointment = (id: string) => {
    sendRequest(CoreAPI_URLs.APPOINTMENTS_GENERAL_OPERATIONS, onGetAppointment, "GET", undefined, [
      {
        header: HeaderEnum.RESOURCE_ID,
        value: id,
      },
    ]);
  };

  const onGetAppointment = (appointment: AppointmentInterface) => {
    // Check if appointment is in a finalized status or edit window has passed
    const isReadOnly = checkStatus(appointment.visitStatus) || isAppointmentEditWindowPassed(appointment);
    setReadOnlyForm(isReadOnly);
    // When the progress note is closed/read-only, prescriptions remain editable only for provider, head nurse, or root users;
    // all other (non-privileged) users are fully read-only for both the form and prescriptions.
    if (isProvider || isHeadNurse || isRoot) {
      setReadOnlyPrescriptions(false);
    } else {
      setReadOnlyForm(true);
      setReadOnlyPrescriptions(true);
    }
    setAppointmentModel(appointment);
    setPersonModel(appointment.patient.person);
    setDiagnosis(appointment.diagnosis || "");
    appointmentId = appointment.id;
    if (appointment.progressNote) {
      setProgressNoteModel(appointment.progressNote);
      setAction(FormActionType.MODIFY);
    } else {
      setProgressNoteModel(getNewProgressNoteDataContent());
      setAction(FormActionType.NEW);
    }

  };

  const toBeImplemented = "Soon to be implemented...";

  const fetchFilerTag = async (appointmentId: string) => {
    if (!appointmentId) return;
    const result = await sendRequest(
      CoreAPI_URLs.PRESCRIPTION_GENERAL_OPERATION,
      null,
      "GET",
      undefined,
      [
        {
          header: "x-resource-id",
          value: appointmentId,
        },
      ]
    );

    const filertag = result?.data;
    const medicationDesc = filertag?.find((r) => r.type === PrescriptionTypeEnum.MEDICINE_PRESCRIPTION)?.description || "";
    const labDesc = filertag?.find((r) => r.type === PrescriptionTypeEnum.LAB_ORDER)?.description || "";
    const imagingDesc = filertag?.find((r) => r.type === PrescriptionTypeEnum.IMAGING_ORDER)?.description || "";

    setMedicationText(medicationDesc);
    setLabText(labDesc);
    setImagingText(imagingDesc);

    // Store original values for comparison
    setOriginalMedicationText(medicationDesc);
    setOriginalLabText(labDesc);
    setOriginalImagingText(imagingDesc);

    setNotesMedicine(
      filertag?.find((r) => r.type === PrescriptionTypeEnum.MEDICINE_PRESCRIPTION)?.notes || ""
    );
    setNotesLab(filertag?.find((r) => r.type === PrescriptionTypeEnum.LAB_ORDER)?.notes || "");
    setNotesImaging(
      filertag?.find((r) => r.type === PrescriptionTypeEnum.IMAGING_ORDER)?.notes || ""
    );
  };

  const checkStatus = (appointmentStatus: VisitStatusEnum) => {
    switch (appointmentStatus) {
      case VisitStatusEnum.CANCEL:
      case VisitStatusEnum.NO_SHOW:
      case VisitStatusEnum.CHECK_OUT:
        setPreview(false);
        return true;
      default:
        setPreview(true);
        return false;
    }
  };

  // Helper to check if the appointment edit window has passed (arrivalDateTime + duration + extension)
  const isAppointmentEditWindowPassed = (appointment: AppointmentInterface) => {
    if (!appointment.arrivalDateTime || !appointment.duration) {
      return false;
    }
    const now = new Date();
    const arrivalTime = new Date(appointment.arrivalDateTime);
    const durationMinutes = (appointment.duration || 0) + timeExtension;
    const appointmentEndTime = new Date(arrivalTime.getTime() + durationMinutes * 60000);
    return appointmentEndTime < now;
  };

  // Helper to get remaining time in minutes
  const getRemainingMinutes = (appointment: Partial<AppointmentInterface>): number => {
    if (!appointment.arrivalDateTime || !appointment.duration) {
      return Infinity;
    }
    const now = new Date();
    const arrivalTime = new Date(appointment.arrivalDateTime);
    const durationMinutes = (appointment.duration || 0) + timeExtension;
    const appointmentEndTime = new Date(arrivalTime.getTime() + durationMinutes * 60000);
    const remainingMs = appointmentEndTime.getTime() - now.getTime();
    return Math.floor(remainingMs / 60000);
  };

  useEffect(() => {
    if (progressNoteModel) {
      setChiefComplaints(progressNoteModel.chiefComplaints);
      setHpiText(progressNoteModel.hpi);
      setAddHpi(true);
      setComments(progressNoteModel.comments);
      fetchFilerTag(appointmentId);
      setAddMedication(true);
      setAddLab(true);
      setAddImaging(true);
    }
  }, [progressNoteModel]);

  // Timer to monitor appointment duration and show warning
  useEffect(() => {
    if (!appointmentModel || readOnlyForm || !appointmentModel.arrivalDateTime || !appointmentModel.duration) {
      return;
    }

    // Initial check
    const checkTime = () => {
      const remainingMinutes = getRemainingMinutes(appointmentModel);

      // Show warning when 1 minute or less remains
      if (remainingMinutes <= 1 && remainingMinutes > 0 && !showTimeWarningModal) {
        setShowTimeWarningModal(true);
      }

      // If time has run out and modal wasn't shown, show it
      if (remainingMinutes <= 0 && !showTimeWarningModal) {
        setShowTimeWarningModal(true);
      }
    };

    // Check immediately
    checkTime();

    // Then check every 30 seconds
    const checkInterval = setInterval(checkTime, 30000);

    return () => {
      clearInterval(checkInterval);
    };
  }, [appointmentModel, readOnlyForm, timeExtension, showTimeWarningModal]);

  const savePrescription = async (
    description: string,
    appointmentId: string,
    type: string,
    note?: string
  ): Promise<boolean> => {
    try {
      await sendRequest(
        CoreAPI_URLs.PRESCRIPTION_NEW_VERSION,
        null,
        "POST",
        JSON.stringify({
          description,
          type,
          notes: note || "",
          reasonForChange: reasonForChange || "",
        }),
        [{ header: HeaderEnum.RESOURCE_ID, value: appointmentId }],
        { successMessage: t("progressNoteForm.prescriptionVersionCreated") }
      );
      return true;
    } catch (error: any) {
      if (error?.message?.includes('PRESCRIPTION_NO_CHANGES') ||
        error?.message?.includes('No changes detected in prescription')) {
        return false;
      }
    }
  };

  const saveInsuranceTransaction = async (billingsEncounters: BillingEncounterInterface[]) => {
    for (const billingEncounter of billingsEncounters) {
      if (!billingEncounter.appointment?.patientInsurance) {
        continue;
      }

      const now = new Date();
      const year = now.getFullYear().toString().slice(-2);
      const random = Math.floor(Math.random() * 100).toString().padStart(2, "0");
      const folio = `FOL-${year}${(now.getMonth() + 1).toString().padStart(2, "0")}${now.getDate().toString().padStart(2, "0")}${now.getHours().toString().padStart(2, "0")}${now.getMinutes().toString().padStart(2, "0")}${now.getSeconds().toString().padStart(2, "0")}${random}`;
      const coPay: number = billingEncounter.appointment.overrideCopay ?? billingEncounter.appointment.patientInsurance.coPay;
      const coInsuranceAmount: number = ((billingEncounter.price ?? 0) - coPay) * ((billingEncounter.appointment.patientInsurance.coInsurance ?? 0) / 100);
      const insuranceTransaction: Partial<InsuranceTransactionInterface> = {
        patientInsurance: { id: billingEncounter.appointment.patientInsurance.id },
        folio: folio,
        copayAmount: coPay,
        totalAmount: billingEncounter.price ?? 0,
        claim: billingEncounter.price - coPay - coInsuranceAmount,
        status: TransactionStatusEnum.PENDING,
        coinsurancePercentage: billingEncounter.appointment.patientInsurance.coInsurance ?? 0,
      };
      await sendRequest(
        CoreAPI_URLs.INSURANCE_TRANSACTION_GENERAL_OPERATION,
        null,
        "POST",
        JSON.stringify(insuranceTransaction),
        null,
        {
          applyErrorFn: (_message) => setError(t("progressNoteForm.missingInsurnace")),
          successMessage: t("progressNoteForm.messageSuccess")
        }
      );
    }
  };

  const savePatientEncounter = async (billingsEncounters: BillingEncounterInterface[]) => {
    for (const billingEncounter of billingsEncounters) {
      try {
        // Get patient ID from appointment
        const patientId = billingEncounter.appointment?.patient?.id || appointmentModel?.patient?.id;
        if (!patientId) {
          console.error("No patient ID found for billing encounter");
          continue;
        }

        // Fetch accounting for patient
        const accountingResponse = await sendRequest<{ results: AccountingInterface }>(
          CoreAPI_URLs.ACCOUNTING_BY_PATIENT_ID,
          (res) => res,
          "GET",
          null,
          [{ header: HeaderEnum.RESOURCE_ID, value: patientId }]
        );

        const accounting = accountingResponse?.data?.results || accountingResponse?.data;
        if (!accounting?.id) {
          console.error("No accounting found for patient");
          continue;
        }

        // Fetch last accounting movement to get current totals
        let lastTotalAmount = 0;
        let lastAmountDue = 0;

        const movementsResponse = await sendRequest<QueryResultInterface<AccountingMovementInterface>>(
          `${CoreAPI_URLs.ACCOUNTING_MOVEMENT_GENERAL_OPERATIONS}/all`,
          (res) => res,
          "GET",
          null,
          [{ header: HeaderEnum.PATIENT_ACCOUNT_NUMBER, value: accounting.id }]
        );

        const movements = movementsResponse?.data?.results || [];
        if (movements.length > 0) {
          const lastMovement = movements[0]; // Movements ordered by date DESC
          lastTotalAmount = lastMovement.totalAmount ?? 0;
          lastAmountDue = lastMovement.amountDue ?? 0;
        }

        // Calculate new totals
        const encounterPrice = billingEncounter.price ?? 0;
        const newTotalAmount = lastTotalAmount + encounterPrice;
        const newAmountDue = lastAmountDue + encounterPrice;

        // Create accounting movement
        const currentDate = new Date();
        const accountMovementPayload = {
          accounting: { id: accounting.id },
          billingEncounter: { id: billingEncounter.id },
          payment: null,
          date: currentDate.toISOString(),
          dueDate: currentDate.toISOString(),
          totalAmount: newTotalAmount,
          amountDue: newAmountDue,
        };

        await sendRequest<AccountingMovementInterface, void>(
          CoreAPI_URLs.ACCOUNTING_MOVEMENT_GENERAL_OPERATIONS,
          null,
          "POST",
          JSON.stringify(accountMovementPayload),
          [{ header: HeaderEnum.PATIENT_ACCOUNT_NUMBER, value: accounting.id }],
          { successMessage: t("progressNoteForm.accountingMovementCreated") }
        );
      } catch (error) {
        console.error("Error creating accounting movement for billing encounter:", error);
      }
    }
  };


  const saveProgressNote = () => {
    const stripHtml = (html) => {
      const tmp = document.createElement('div');
      tmp.innerHTML = html || '';
      return tmp.textContent?.trim() || '';
    };

    const medChanged = medicationText && stripHtml(medicationText) !== stripHtml(originalMedicationText);
    const labChanged = labText && stripHtml(labText) !== stripHtml(originalLabText);
    const imgChanged = imagingText && stripHtml(imagingText) !== stripHtml(originalImagingText);

    if (progressNoteModel?.id && (medChanged || labChanged || imgChanged)) {
      setOpenPrescriptionVersionModal(true);
      return;
    }
    if (chiefComplaints && hpiText && appointmentId) {
      progressNoteModel.chiefComplaints = chiefComplaints;
      progressNoteModel.hpi = hpiText;
      progressNoteModel.comments = comments || "";
      progressNoteModel.hpiTitle = "Default";
      progressNoteModel.hpiVersion = "1.0";
      if (hpiTemplateInfo) {
        progressNoteModel.hpiContentType = hpiTemplateInfo.contentType;
        progressNoteModel.hpiVersion = hpiTemplateInfo.version;
      }
      let method: Method;
      let message: string;
      let resourceId: string;
      if (action === FormActionType.NEW) {
        method = "POST";
        message = "The Progress note was created";
        resourceId = appointmentId;
      } else if (action === FormActionType.MODIFY) {
        method = "PUT";
        message = "The Progress note was updated";
        resourceId = progressNoteModel.id;
      }
      sendRequest(
        CoreAPI_URLs.PROGRESS_NOTE_GENERAL_OPERATION,
        onProgressNoteSaveHandler,
        method,
        JSON.stringify(progressNoteModel),
        [{ header: HeaderEnum.RESOURCE_ID, value: resourceId }],
        { successMessage: message }
      );
      if (billingEncounter) {
        saveInsuranceTransaction(billingEncounter.filter(b => b.isClaim))
        savePatientEncounter(billingEncounter.filter(b => !b.isClaim) )
      }
    }
    if (!progressNoteModel?.id && appointmentId) {
      const savePromises = [];

      if (medicationText) {
        savePromises.push(
          savePrescription(
            medicationText,
            appointmentId,
            PrescriptionTypeEnum.MEDICINE_PRESCRIPTION,
            notesMedicine
          )
        );
      }
      if (labText) {
        savePromises.push(
          savePrescription(labText, appointmentId, PrescriptionTypeEnum.LAB_ORDER, notesLab)
        );
      }
      if (imagingText) {
        savePromises.push(
          savePrescription(
            imagingText,
            appointmentId,
            PrescriptionTypeEnum.IMAGING_ORDER,
            notesImaging
          )
        );
      }
      if (savePromises.length > 0) {
        Promise.all(savePromises)
          .then(() => {
            setTimeout(() => {
              fetchFilerTag(appointmentId);
            }, 800);
          })
          .catch(error => {
            console.error('Error storing prescriptions', error);
          });
      }
    }
  };

  const closeAppointment = (progressNoteData: ProgressNoteInterface) => {
    appointmentModel.visitStatus = VisitStatusEnum.CHECK_OUT;
    appointmentModel.progressNote = progressNoteData;
    appointmentModel.vitals ??= undefined;
    appointmentModel.diagnosis = diagnosis;
    sendRequest(
      CoreAPI_URLs.APPOINTMENTS_GENERAL_OPERATIONS,
      null,
      "PUT",
      JSON.stringify(appointmentModel),
      [{ header: HeaderEnum.RESOURCE_ID, value: appointmentModel.id }],
      { successMessage: t("progressNoteForm.appointmentCheckedOut") }
    );
  };

  const handleAutoCloseWithoutSave = () => {
    setShowTimeWarningModal(false);
    // Just close the form without saving
    setReadOnlyForm(true);
  };

  const handleContinueWorking = () => {
    setTimeExtension(prev => prev + 10);
    setShowTimeWarningModal(false);
  };

  const handleStopWorking = () => {
    // Check if required fields are filled
    const hasChiefComplaints = chiefComplaints && chiefComplaints.trim().length > 0;
    const hasHpiText = hpiText && hpiText.trim().length > 0;

    // Case 1: Both required fields are empty - close without saving
    if (!hasChiefComplaints && !hasHpiText) {
      setShowTimeWarningModal(false);
      setReadOnlyForm(true);
      return;
    }

    // Case 2: Missing required fields - extend time and show validation
    if (!hasChiefComplaints || !hasHpiText) {
      // Extend time by 10 minutes automatically
      setTimeExtension(prev => prev + 10);
      setShowTimeWarningModal(false);

      // Set error state to trigger validation styling
      setError(t("progressNoteForm.requiredFieldsMissing"));

      // Scroll to the missing field
      if (!hasChiefComplaints) {
        const element = document.getElementById("cy-chiefComplaint");
        element?.scrollIntoView({ behavior: "smooth", block: "center" });
        element?.focus();
      } else if (!hasHpiText) {
        const element = document.getElementById("cy-hpi");
        element?.scrollIntoView({ behavior: "smooth", block: "center" });
      }

      return;
    }

    // Case 3: All required fields are filled - save and close
    setShowTimeWarningModal(false);

    const progressNoteData = {
      ...progressNoteModel,
      chiefComplaints,
      hpi: hpiText,
      comments,
      registrationDate: progressNoteModel.registrationDate || new Date(),
    };

    if (action === FormActionType.NEW) {
      sendRequest(
        CoreAPI_URLs.PROGRESS_NOTE_GENERAL_OPERATION,
        onProgressNoteSaveHandler,
        "POST",
        JSON.stringify(progressNoteData),
        [{ header: HeaderEnum.RESOURCE_ID, value: appointmentId }],
        { successMessage: t("progressNoteForm.progressNoteSaved") }
      );
    } else {
      sendRequest(
        CoreAPI_URLs.PROGRESS_NOTE_GENERAL_OPERATION,
        onProgressNoteSaveHandler,
        "PUT",
        JSON.stringify(progressNoteData),
        [{ header: HeaderEnum.RESOURCE_ID, value: progressNoteModel.id }],
        { successMessage: t("progressNoteForm.progressNoteUpdated") }
      );
    }

    // Save prescriptions if any
    saveProgressNote();
  };

  const onProgressNoteSaveHandler = (progressNoteData: ProgressNoteInterface) => {
    setSaveVitals(true);
    saveVitalsRef.current = true;
    closeAppointment(progressNoteData);
    setTimeout(() => {
      searchAppointment(appointmentId);
    }, 500);
    setProgressNoteModel(progressNoteData);
    setPreview(false);
  };
  const handleChangeDiagnosis = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setDiagnosis(event.target.value);
  };
  const handleChangeNotesMedicine = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setNotesMedicine(event.target.value);
  };
  const handleChangeNotesLab = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setNotesLab(event.target.value);
  };
  const handleChangeNotesImaging = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setNotesImaging(event.target.value);
  };
  const handleChangeChiefComplaints = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setChiefComplaints(event.target.value);
    // Clear error when user starts filling the field
    if (error && event.target.value.trim().length > 0) {
      setError("");
    }
  };
  const handleChangeComments = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setComments(event.target.value);
  };

  const onGetPrescriptionHandler = (FilerTagResponse: QueryResultInterface<TemplateInterface>) => {
    const tags = FilerTagResponse.results
      .flatMap((element) => element.tags || [])
      .filter((tag): tag is string => Boolean(tag));
    const uniqueTags = [...new Set(tags)];
    const filteredTags = uniqueTags.filter(
      (tag) => tag !== "Lab Order" && tag !== "Imaging Order" && tag !== "Medicine Prescription"
    );
    const tagObjects = filteredTags.map((tag) => ({
      id: crypto.randomUUID(),
      name: tag,
    }));
    setFilterTagListResponse(tagObjects);
  };

  const handleSelectChange = (FilerTag: TagsInterface[]) => {
    setSelectedFilerTag(FilerTag);
  };

  const prescriptionFilterTemplate = (
    setText: React.Dispatch<React.SetStateAction<string>>,
    setAdd: React.Dispatch<React.SetStateAction<boolean>>,
    customTag: string
  ) => {
    return (
      <PrescriptionTemplatesDataContent
        selectFunction={selectPrescriptionTemplate(setText, setAdd)}
        readOnly={true}
        customTag={customTag}
        filterTag={selectedFilerTag}
      />
    );
  };

  useEffect(() => {
    sendRequest(CoreAPI_URLs.HPI_TEMPLATE_GET_ALL, onGetPrescriptionHandler, "GET");
  }, []);

  const selectHpiTemplate = (selectedHpi: TemplateInterface) => {
    setHpiText((prevHpi) =>
      prevHpi ? prevHpi + selectedHpi.template + "\n" : selectedHpi.template + "\n"
    );
    setHpiTemplateInfo(selectedHpi);
    setAddHpi(true);
  };

  const [expandedPanels, setExpandedPanels] = useState([]);

  const handlePanelChange = (panel) => (_, isExpanded) => {
    setExpandedPanels(
      isExpanded ? [...expandedPanels, panel] : expandedPanels.filter((p) => p !== panel)
    );
  };

  const handleCollapseAll = () => {
    setExpandedPanels([]);
  };

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSectionsToPrint([]);
  };

  const handleOpenBillingModal = () => {
    setIsBillingModalOpen(true);
  }

  const handleCloseBillingModal = () => {
    setIsBillingModalOpen(false);
    searchBillings();
  }

  const handleSavePrescriptionVersions = async () => {
    let hasChanges = false;
    const stripHtml = (html) => {
      const tmp = document.createElement('div');
      tmp.innerHTML = html || '';
      return tmp.textContent?.trim() || '';
    };

    // Save only modified prescriptions as new versions
    const medChanged = medicationText && stripHtml(medicationText) !== stripHtml(originalMedicationText);
    if (medChanged && appointmentId) {
      const saved = await savePrescription(
        medicationText,
        appointmentId,
        PrescriptionTypeEnum.MEDICINE_PRESCRIPTION,
        notesMedicine
      );
      if (saved) hasChanges = true;
    }
    const labChanged = labText && stripHtml(labText) !== stripHtml(originalLabText);
    if (labChanged && appointmentId) {
      const saved = await savePrescription(labText, appointmentId, PrescriptionTypeEnum.LAB_ORDER, notesLab);
      if (saved) hasChanges = true;
    }
    const imgChanged = imagingText && stripHtml(imagingText) !== stripHtml(originalImagingText);
    if (imgChanged && appointmentId) {
      const saved = await savePrescription(
        imagingText,
        appointmentId,
        PrescriptionTypeEnum.IMAGING_ORDER,
        notesImaging
      );
      if (saved) hasChanges = true;
    }

    // Close modal and reset reason
    setOpenPrescriptionVersionModal(false);
    setReasonForChange("");

    // Refresh data and reset original values only if there were actual changes
    if (hasChanges) {
      setTimeout(() => {
        searchAppointment(appointmentId);
        // Reset original values to new values after save
        setOriginalMedicationText(medicationText);
        setOriginalLabText(labText);
        setOriginalImagingText(imagingText);
      }, 500);
    }
  };

  const handleCancelPrescriptionVersion = () => {
    setOpenPrescriptionVersionModal(false);
    setReasonForChange("");
  };

  const handleOpenPrescriptionHistory = () => {
    setOpenPrescriptionHistoryModal(true);
  };

  const handleClosePrescriptionHistory = () => {
    setOpenPrescriptionHistoryModal(false);
  };

  useEffect(() => {
    console.log("hpiText changed:", hpiText);
    // Clear error when user fills HPI
    if (error && hpiText && hpiText.trim().length > 0) {
      setError("");
    }
  }, [hpiText]);

  useEffect(() => {
    console.log("addHpi changed:", addHpi);
  }, [addHpi]);
  const accordionsList = [
    { id: "chief-complaints", name: t("progressNoteForm.chiefComplaints") },
    { id: "hpi", name: t("progressNoteForm.hpi") },
    { id: "vitals", name: t("progressNoteForm.vitals") },
    { id: "comments", name: t("progressNoteForm.comments") },
    /*TODO - All ids from others sections
    { id: "assessment-diagnosis", name: "Assessment / Diagnosis" },
    { id: "allergies-intolerance", name: "Allergies / Intolerance" },
    { id: "vaccine", name: "Vaccine" },
    { id: "patient-history", name: "Patient History" },
    { id: "current-visit", name: "Current Visit" },
    { id: "treatment", name: "Treatment" },
    { id: "medication", name: "Medication" },
    { id: "pharmacy", name: "Pharmacy" },
    { id: "care-plan", name: "Care Plan" },
    { id: "health-risk", name: "Health Risk" },
    { id: "preventine-medicine", name: "Preventive Medicine" },
    { id: "aditional-notes", name: "Additional Notes" },*/
  ];

  const handleListPrint = (e: string[]) => {
    setSectionsToPrint(e);
  };

  return (
    <>
      {!historyActive && (
        <Box mr={2} mb={2} textAlign={"right"} display="flex" justifyContent="flex-end" gap={1}>
          <PermissionWrapper
            requiredPermissions={[PermissionsUser[EntityPermissions.PROGRESS_NOTE].PRINT]}
          >
            <Button
              onClick={handleOpenModal}
              variant="contained"
              color={preview ? "dark" : "primary"}
              disabled={preview}
            >
              {t("progressNoteForm.printPreview")}
            </Button>
          </PermissionWrapper>
          <PermissionWrapper
            requiredPermissions={[PermissionsUser[EntityPermissions.PROGRESS_NOTE].PRINT]}
          >
            <Button
              onClick={() => setOpenPrescriptionModal(true)}
              variant="contained"
              color={preview ? "dark" : "info"}
            >
              {t("progressNoteForm.printPrescription")}
            </Button>
          </PermissionWrapper>
        </Box>
      )}
      <Box borderRadius="lg">
        {!historyActive && personModel && (
          <HeaderPersonInformation personInformation={personModel} showPhoto />
        )}
        <Typography paddingTop={2} variant="h3">
          {t("progressNoteForm.progressNote")}
        </Typography>
        <IconButton
          onClick={handleCollapseAll}
          aria-label="Collapse All"
          style={{
            position: "fixed",
            bottom: "2rem",
            right: "6rem",
            zIndex: 1000,
            backgroundColor: "white",
            boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)",
          }}
          color="info"
        >
          <UnfoldLessDoubleIcon />
        </IconButton>
        <Box sx={{ width: "100%", mb: 2, mt: 1 }}>
          <AutocompleteCheckboxes
            options={FilterTagListResponse}
            label={t("prescriptionTemplateForm.selectTags")}
            placeholder={t("prescriptionTemplateForm.chooseTags")}
            onChange={(selected) => {
              handleSelectChange(selected);
            }}
            value={selectedFilerTag}
            optionColors={optionColors}
            setOptionColors={setOptionColors}
            getOptionLabel={(option) => option.name}
          />
        </Box>
        <Accordion
          className="data-accordion"
          expanded={expandedPanels.includes("panel-diagnosis")}
          onChange={handlePanelChange("panel-diagnosis")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel-diagnosis-content"
            id="panel-diagnosis-header"
          >
            {t("progressNoteForm.diagnosis")}
          </AccordionSummary>
          <AccordionDetails>
            <FormInput
              id="cy-diagnosis"
              style={{ marginBottom: "1rem" }}
              InputProps={{ readOnly: readOnlyForm }}
              label={t("progressNoteForm.diagnosis")}
              fullWidth
              multiline
              rows={5}
              value={diagnosis}
              onChange={handleChangeDiagnosis}
            />
          </AccordionDetails>
        </Accordion>
        <Accordion
          className="data-accordion"
          expanded={expandedPanels.includes("panel-chiefcomplaints")}
          onChange={handlePanelChange("panel-chiefcomplaints")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel-chiefcomplaints-content"
            id="panel-chiefcomplaints-header"
          >
            {t("progressNoteForm.chiefComplaints")}
          </AccordionSummary>
          <AccordionDetails>
            <FormInput
              id="cy-chiefComplaint"
              style={{ marginBottom: "1rem" }}
              InputProps={{ readOnly: readOnlyForm }}
              label={t("progressNoteForm.chiefComplaints")}
              fullWidth
              multiline
              rows={5}
              value={chiefComplaints}
              onChange={handleChangeChiefComplaints}
              error={error && (!chiefComplaints || chiefComplaints.trim().length === 0)}
              helperText={error && (!chiefComplaints || chiefComplaints.trim().length === 0) ? t("progressNoteForm.fieldRequired") : ""}
            />
          </AccordionDetails>
        </Accordion>
        <Accordion
          className="data-accordion"
          expanded={expandedPanels.includes("panel-hpi")}
          onChange={handlePanelChange("panel-hpi")}
          sx={{
            border: error && (!hpiText || hpiText.trim().length === 0) ? '2px solid red' : 'none'
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel-hpi-content"
            id="panel-hpi-header"
          >
            {t("progressNoteForm.hpi")}
          </AccordionSummary>
          <AccordionDetails>
            <Box mb={3}>
              {readOnlyForm ? (
                <Grid container spacing={0} maxWidth="calc(100% - 2rem)">
                  <Grid item lg={12} id="cy-hpi">
                    <FormTextEditor
                      value={hpiText}
                      setFieldValue={setHpiText}
                      addExternal={addHpi}
                      setAddExternal={setAddHpi}
                      editorHeight="30rem"
                      wrapperHeight="40rem"
                      readOnly={readOnlyForm}
                    />
                  </Grid>
                </Grid>
              ) : (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={12} lg={historyActive ? 12 : 6} id="cy-hpi">
                    <FormTextEditor
                      inputId="cy-hpi"
                      value={hpiText}
                      setFieldValue={setHpiText}
                      addExternal={addHpi}
                      setAddExternal={setAddHpi}
                      editorHeight="30rem"
                      wrapperHeight="40rem"
                      readOnly={readOnlyForm}

                    />
                  </Grid>
                  <Grid item xs={12} md={12} lg={historyActive ? 12 : 6} overflow="auto">
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems={{ xs: "flex-end", sm: "center" }}
                      flexDirection={{ xs: "column", sm: "row" }}
                      mb={2}
                      overflow="auto"
                    >
                      <HPITemplatesDataContent
                        filterTag={selectedFilerTag}
                        selectFunction={selectHpiTemplate}
                        readOnly={true}
                      />
                    </Box>
                  </Grid>
                </Grid>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>
        <Accordion
          className="data-accordion"
          expanded={expandedPanels.includes("panel-vitals")}
          onChange={handlePanelChange("panel-vitals")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel-vitals-content"
            id="panel-vitals-header"
          >
            {t("progressNoteForm.vitals")}
          </AccordionSummary>
          <AccordionDetails>
            {appointmentModel.id && (
              <VitalsDataContent
                selectedAppointment={appointmentModel}
                fromProgressNote={saveVitalsRef.current}
                setSaveVitals={setSaveVitals}
                readOnlyForm={readOnlyForm}
              />
            )}
          </AccordionDetails>
        </Accordion>
        <Accordion
          className="data-accordion"
          expanded={expandedPanels.includes("panel-comments")}
          onChange={handlePanelChange("panel-comments")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel-comments-content"
            id="panel-comments-header"
          >
            {t("progressNoteForm.comments")}
          </AccordionSummary>
          <AccordionDetails>
            <FormInput
              style={{ marginBottom: "1rem" }}
              InputProps={{ readOnly: readOnlyForm }}
              label={t("progressNoteForm.comments")}
              fullWidth
              multiline
              rows={5}
              value={comments}
              onChange={handleChangeComments}
            />
          </AccordionDetails>
        </Accordion>
        <Accordion
          className="data-accordion"
          expanded={expandedPanels.includes("panel-prescription")}
          onChange={handlePanelChange("panel-prescription")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel-prescription-content"
            id="panel-prescription-header"
          >
            {t("progressNoteForm.prescription")}
          </AccordionSummary>
          <AccordionDetails>
            {appointmentId && !isPatient && (
              originalMedicationText || originalLabText || originalImagingText
            ) && (
                <Box mb={2} textAlign="right">
                  <Button
                    variant="outlined"
                    color="info"
                    onClick={handleOpenPrescriptionHistory}
                    startIcon={<HistoryIcon />}
                  >
                    {t("progressNoteForm.prescriptionHistory")}
                  </Button>
                </Box>
              )}
            <Accordion
              className="data-accordion"
              expanded={expandedPanels.includes("panel-medication")}
              onChange={handlePanelChange("panel-medication")}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel-medication-content"
                id="panel-medication-header"
              >
                {t("progressNoteForm.medication")}
              </AccordionSummary>
              <AccordionDetails>
                <FormInput
                  style={{ marginBottom: "1rem" }}
                  InputProps={{ readOnly: readOnlyPrescriptions }}
                  label={t("progressNoteForm.notes")}
                  fullWidth
                  multiline
                  rows={5}
                  value={notesMedicine}
                  onChange={handleChangeNotesMedicine}
                />
                {readOnlyPrescriptions ? (
                  <Grid container spacing={0} maxWidth="calc(100% - 2rem)">
                    <Grid item lg={12}>
                      <FormTextEditor
                        value={medicationText}
                        setFieldValue={setMedicationText}
                        addExternal={addMedication}
                        setAddExternal={setAddMedication}
                        editorHeight="30rem"
                        wrapperHeight="40rem"
                        readOnly={readOnlyPrescriptions}
                      />
                    </Grid>
                  </Grid>
                ) : (
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={12} lg={historyActive ? 12 : 6}>
                      <FormTextEditor
                        inputId="cy-medication"
                        value={medicationText}
                        setFieldValue={setMedicationText}
                        addExternal={addMedication}
                        setAddExternal={setAddMedication}
                        editorHeight="30rem"
                        wrapperHeight="40rem"
                        readOnly={readOnlyPrescriptions}
                      />
                    </Grid>
                    <Grid item xs={12} md={12} lg={historyActive ? 12 : 6} overflow="auto">
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems={{ xs: "flex-end", sm: "center" }}
                        flexDirection={{ xs: "column", sm: "row" }}
                        mb={2}
                        overflow="auto"
                      >
                        {prescriptionFilterTemplate(
                          setMedicationText,
                          setAddMedication,
                          PrescriptionTypeEnum.MEDICINE_PRESCRIPTION
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                )}
              </AccordionDetails>
            </Accordion>
            <Accordion
              className="data-accordion"
              expanded={expandedPanels.includes("panel-labTest")}
              onChange={handlePanelChange("panel-labTest")}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel-labTest-content"
                id="panel-labTest-header"
              >
                {t("progressNoteForm.labTest")}
              </AccordionSummary>
              <AccordionDetails>
                <FormInput
                  style={{ marginBottom: "1rem" }}
                  InputProps={{ readOnly: readOnlyPrescriptions }}
                  label={t("progressNoteForm.notes")}
                  fullWidth
                  multiline
                  rows={5}
                  value={notesLab}
                  onChange={handleChangeNotesLab}
                />
                {readOnlyPrescriptions ? (
                  <Grid container spacing={0} maxWidth="calc(100% - 2rem)">
                    <Grid item lg={12}>
                      <FormTextEditor
                        value={labText}
                        setFieldValue={setLabText}
                        addExternal={addLab}
                        setAddExternal={setAddLab}
                        editorHeight="30rem"
                        wrapperHeight="40rem"
                        readOnly={readOnlyPrescriptions}
                      />
                    </Grid>
                  </Grid>
                ) : (
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={12} lg={historyActive ? 12 : 6}>
                      <FormTextEditor
                        inputId="cy-labTest"
                        value={labText}
                        setFieldValue={setLabText}
                        addExternal={addLab}
                        setAddExternal={setAddLab}
                        editorHeight="30rem"
                        wrapperHeight="40rem"
                        readOnly={readOnlyPrescriptions}
                      />
                    </Grid>
                    <Grid item xs={12} md={12} lg={historyActive ? 12 : 6} overflow="auto">
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems={{ xs: "flex-end", sm: "center" }}
                        flexDirection={{ xs: "column", sm: "row" }}
                        mb={2}
                        overflow="auto"
                      >
                        {prescriptionFilterTemplate(
                          setLabText,
                          setAddLab,
                          PrescriptionTypeEnum.LAB_ORDER
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                )}
              </AccordionDetails>
            </Accordion>
            <Accordion
              className="data-accordion"
              expanded={expandedPanels.includes("panel-imagingTest")}
              onChange={handlePanelChange("panel-imagingTest")}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel-imagingTest-content"
                id="panel-imagingTest-header"
              >
                {t("progressNoteForm.imagingTest")}
              </AccordionSummary>
              <AccordionDetails>
                <FormInput
                  style={{ marginBottom: "1rem" }}
                  InputProps={{ readOnly: readOnlyPrescriptions }}
                  label={t("progressNoteForm.notes")}
                  fullWidth
                  multiline
                  rows={5}
                  value={notesImaging}
                  onChange={handleChangeNotesImaging}
                />
                {readOnlyPrescriptions ? (
                  <Grid container spacing={0} maxWidth="calc(100% - 2rem)">
                    <Grid item lg={12}>
                      <FormTextEditor
                        value={imagingText}
                        setFieldValue={setImagingText}
                        addExternal={addImaging}
                        setAddExternal={setAddImaging}
                        editorHeight="30rem"
                        wrapperHeight="40rem"
                        readOnly={readOnlyPrescriptions}
                      />
                    </Grid>
                  </Grid>
                ) : (
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={12} lg={historyActive ? 12 : 6}>
                      <FormTextEditor
                        inputId="cy-imagingTest"
                        value={imagingText}
                        setFieldValue={setImagingText}
                        addExternal={addImaging}
                        setAddExternal={setAddImaging}
                        editorHeight="30rem"
                        wrapperHeight="40rem"
                        readOnly={readOnlyPrescriptions}
                      />
                    </Grid>
                    <Grid item xs={12} md={12} lg={historyActive ? 12 : 6} overflow="auto">
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems={{ xs: "flex-end", sm: "center" }}
                        flexDirection={{ xs: "column", sm: "row" }}
                        mb={2}
                        overflow="auto"
                      >
                        {prescriptionFilterTemplate(
                          setImagingText,
                          setAddImaging,
                          PrescriptionTypeEnum.IMAGING_ORDER
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                )}
              </AccordionDetails>
            </Accordion>
          </AccordionDetails>
        </Accordion>
        <Accordion
          className="data-accordion"
          expanded={expandedPanels.includes("panel-billingEncounter")}
          onChange={handlePanelChange("panel-billingEncounter")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel-billingEncounter-content"
            id="panel-billingEncounter-header"
          >
            {t("progressNoteForm.billingEncounter")}
          </AccordionSummary>
          <AccordionDetails>
            {(!readOnlyForm && (appointmentId || appointmentIdFromState)) && (
              <PermissionWrapper
                requiredPermissions={[PermissionsUser[EntityPermissions.BILLING_ENCOUNTER].CREATE]}
              >
                <Button
                  id="cy-add-billing-button"
                  style={{ display: !progressNoteModel ? "none" : null }}
                  color="success"
                  onClick={handleOpenBillingModal}
                >
                  {t("appointmentForm.createBilling")}
                </Button>
              </PermissionWrapper>
            )}
            {(appointmentId || appointmentIdFromState) && (
              <BillingEncounterTable
                modelList={billingEncounter}
                setModelList={setBillingEncounter}
                setModelFilter={setBillingFilter}
                entriesPerPage={{ defaultValue: pageSize }}
                setPageState={setPage}
                canSearchPlaceholder={t("progressNoteForm.searchByCtp")}
                setPageSizeState={setPageSize}
                apiPagination={true}
                apiPageTotal={pageTotal}
                apiRowsTotal={rowsTotal}
                apiPageCurrentIndex={page}
                compact={true}
                readOnly={readOnlyForm}
              />
            )}
          </AccordionDetails>
        </Accordion>

        {/* TODO - Sections to be implemented 
        <Typography paddingTop={2} variant="h5">
          {toBeImplemented}
        </Typography>
        <Accordion
          className="data-accordion"
          expanded={expandedPanels.includes("panel-assessmentDiagnosis")}
          onChange={handlePanelChange("panel-assessmentDiagnosis")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel-assessmentDiagnosis-content"
            id="assessment-diagnosis-header"
          >
            {t("progressNoteForm.assessmentDiagnosis")}
          </AccordionSummary>
          <AccordionDetails>
            <FormInput
              style={{ marginBottom: "1rem" }}
              label="Summary"
              fullWidth
              multiline
              rows={5}
              value={toBeImplemented}
              onChange={handleChangeComments}
            />
            <FormInput
              style={{ marginBottom: "1rem" }}
              label="Today Diagnosis"
              fullWidth
              multiline
              rows={5}
              value={toBeImplemented}
              onChange={handleChangeComments}
            />
            <FormInput
              style={{ marginBottom: "1rem" }}
              label="Probable/Under Study"
              fullWidth
              multiline
              rows={5}
              value={toBeImplemented}
              onChange={handleChangeComments}
            />
            <FormInput
              style={{ marginBottom: "1rem" }}
              label="Notes"
              fullWidth
              multiline
              rows={5}
              value={toBeImplemented}
              onChange={handleChangeComments}
            />
          </AccordionDetails>
        </Accordion>
        <Accordion
          className="data-accordion"
          expanded={expandedPanels.includes("panel-allergiesIntolerance")}
          onChange={handlePanelChange("panel-allergiesIntolerance")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel-allergiesIntolerance-content"
            id="allergies-intolerance-header"
          >
            {t("progressNoteForm.allergiesIntolerance")}
          </AccordionSummary>
          <AccordionDetails>
            <FormInput
              style={{ marginBottom: "1rem" }}
              label="Allergies Test"
              fullWidth
              multiline
              rows={5}
              value={toBeImplemented}
              onChange={handleChangeComments}
            />
            <FormInput
              style={{ marginBottom: "1rem" }}
              label={t("progressNoteForm.summary")}
              fullWidth
              multiline
              rows={5}
              value={toBeImplemented}
              onChange={handleChangeComments}
            />
            <FormInput
              style={{ marginBottom: "1rem" }}
              label={t("progressNoteForm.today")}
              fullWidth
              multiline
              rows={5}
              value={toBeImplemented}
              onChange={handleChangeComments}
            />
          </AccordionDetails>
        </Accordion>
        <Accordion
          className="data-accordion"
          expanded={expandedPanels.includes("panel-vaccine")}
          onChange={handlePanelChange("panel-vaccine")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel-vaccine-content"
            id="vacine-header"
          >
            {t("progressNoteForm.vaccine")}
          </AccordionSummary>
          <AccordionDetails>
            <FormGroup
              style={{
                marginLeft: "2rem",
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(15rem, 1fr))",
                gap: "1rem",
              }}
            >
              <FormControlLabel control={<CheckBox />} label={t("progressNoteForm.vaccine")} />
              <FormControlLabel control={<CheckBox />} label={t("progressNoteForm.tetanus")} />
              <FormControlLabel control={<CheckBox />} label={t("progressNoteForm.rubella")} />
              <FormControlLabel control={<CheckBox />} label={t("progressNoteForm.measles")} />
              <FormControlLabel control={<CheckBox />} label={t("progressNoteForm.mumps")} />
              <FormControlLabel control={<CheckBox />} label={t("progressNoteForm.hepatitisB")} />
              <FormControlLabel control={<CheckBox />} label={t("progressNoteForm.hepatitisA")} />
              <FormControlLabel control={<CheckBox />} label={t("progressNoteForm.diphtheria")} />
              <FormControlLabel control={<CheckBox />} label={t("progressNoteForm.pertussis")} />
              <FormControlLabel
                control={<CheckBox />}
                label={t("progressNoteForm.haemophilusInfluenzaeTypeB")}
              />
              <FormControlLabel control={<CheckBox />} label={t("progressNoteForm.varicella")} />
              <FormControlLabel
                control={<CheckBox />}
                label={t("progressNoteForm.pneumococcalConjugate")}
              />
              <FormControlLabel
                control={<CheckBox />}
                label={t("progressNoteForm.pneumococcalPolysaccharide")}
              />
              <FormControlLabel control={<CheckBox />} label={t("progressNoteForm.rotavirus")} />
              <FormControlLabel
                control={<CheckBox />}
                label={t("progressNoteForm.humanPapillomavirus")}
              />
              <FormControlLabel
                control={<CheckBox />}
                label={t("progressNoteForm.meningococcalConjugate")}
              />
              <FormControlLabel
                control={<CheckBox />}
                label={t("progressNoteForm.meningococcalB")}
              />
            </FormGroup>
          </AccordionDetails>
        </Accordion>
        <Accordion
          className="data-accordion"
          expanded={expandedPanels.includes("panel-patientHistory")}
          onChange={handlePanelChange("panel-patientHistory")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel-patientHistory-content"
            id="patient-history-header"
          >
            Patient History
          </AccordionSummary>
          <AccordionDetails>
            <FormInput
              style={{ marginBottom: "1rem" }}
              label={t("progressNoteForm.medicalHistory")}
              fullWidth
              multiline
              rows={5}
              value={toBeImplemented}
              onChange={handleChangeComments}
            />
            <FormInput
              style={{ marginBottom: "1rem" }}
              label={t("progressNoteForm.gynHistory")}
              fullWidth
              multiline
              rows={5}
              value={toBeImplemented}
              onChange={handleChangeComments}
            />
            <FormInput
              style={{ marginBottom: "1rem" }}
              label={t("progressNoteForm.obHistory")}
              fullWidth
              multiline
              rows={5}
              value={toBeImplemented}
              onChange={handleChangeComments}
            />
            <FormInput
              style={{ marginBottom: "1rem" }}
              label={t("progressNoteForm.surgicalHistory")}
              fullWidth
              multiline
              rows={5}
              value={toBeImplemented}
              onChange={handleChangeComments}
            />
            <FormInput
              style={{ marginBottom: "1rem" }}
              label={t("progressNoteForm.familyHistory")}
              fullWidth
              multiline
              rows={5}
              value={toBeImplemented}
              onChange={handleChangeComments}
            />
            <FormInput
              style={{ marginBottom: "1rem" }}
              label={t("progressNoteForm.socialHistory")}
              fullWidth
              multiline
              rows={5}
              value={toBeImplemented}
              onChange={handleChangeComments}
            />
            <FormInput
              style={{ marginBottom: "1rem" }}
              label={t("progressNoteForm.hospitalizationHistory")}
              fullWidth
              multiline
              rows={5}
              value={toBeImplemented}
              onChange={handleChangeComments}
            />
          </AccordionDetails>
        </Accordion>
        <Accordion
          className="data-accordion"
          expanded={expandedPanels.includes("panel-currentVisit")}
          onChange={handlePanelChange("panel-currentVisit")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel-currentVisit-content"
            id="current-visit-header"
          >
            {t("progressNoteForm.currentVisit")}
          </AccordionSummary>
          <AccordionDetails>
            <FormInput
              style={{ marginBottom: "1rem" }}
              label="Physical Examination"
              fullWidth
              multiline
              rows={5}
              value={toBeImplemented}
              onChange={handleChangeComments}
            />
          </AccordionDetails>
        </Accordion>
        <Accordion
          className="data-accordion"
          expanded={expandedPanels.includes("panel-treatment")}
          onChange={handlePanelChange("panel-treatment")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel-treatment-content"
            id="treatment-header"
          >
            {t("progressNoteForm.treatment")}
          </AccordionSummary>
          <AccordionDetails>
            <FormInput
              style={{ marginBottom: "1rem" }}
              label={t("progressNoteForm.rx")}
              fullWidth
              multiline
              rows={5}
              value={toBeImplemented}
              onChange={handleChangeComments}
            />
            <FormInput
              style={{ marginBottom: "1rem" }}
              label={t("progressNoteForm.labs")}
              fullWidth
              multiline
              rows={5}
              value={toBeImplemented}
              onChange={handleChangeComments}
            />
            <FormInput
              style={{ marginBottom: "1rem" }}
              label={t("progressNoteForm.imaging")}
              fullWidth
              multiline
              rows={5}
              value={toBeImplemented}
              onChange={handleChangeComments}
            />
            <FormInput
              style={{ marginBottom: "1rem" }}
              label={t("progressNoteForm.procedures")}
              fullWidth
              multiline
              rows={5}
              value={toBeImplemented}
              onChange={handleChangeComments}
            />
            <FormInput
              style={{ marginBottom: "1rem" }}
              label={t("progressNoteForm.referral")}
              fullWidth
              multiline
              rows={5}
              value={toBeImplemented}
              onChange={handleChangeComments}
            />
          </AccordionDetails>
        </Accordion>
        <Accordion
          className="data-accordion"
          expanded={expandedPanels.includes("panel-medicine")}
          onChange={handlePanelChange("panel-medicine")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel-medicine-content"
            id="medication-header"
          >
            {t("progressNoteForm.medication")}
          </AccordionSummary>
          <AccordionDetails>
            <FormInput
              style={{ marginBottom: "1rem" }}
              label={t("progressNoteForm.summary")}
              fullWidth
              multiline
              rows={5}
              value={toBeImplemented}
              onChange={handleChangeComments}
            />
            <FormInput
              style={{ marginBottom: "1rem" }}
              label={t("progressNoteForm.currentTaken")}
              fullWidth
              multiline
              rows={5}
              value={toBeImplemented}
              onChange={handleChangeComments}
            />
            <FormInput
              style={{ marginBottom: "1rem" }}
              label={t("progressNoteForm.today")}
              fullWidth
              multiline
              rows={5}
              value={toBeImplemented}
              onChange={handleChangeComments}
            />
          </AccordionDetails>
        </Accordion>
        <Accordion
          className="data-accordion"
          expanded={expandedPanels.includes("panel-pharmacy")}
          onChange={handlePanelChange("panel-pharmacy")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel-pharmacy-content"
            id="pharmacy-header"
          >
            Pharmacy Recipe
          </AccordionSummary>
          <AccordionDetails>
            <FormInput
              style={{ marginBottom: "1rem" }}
              label={t("progressNoteForm.summary")}
              fullWidth
              multiline
              rows={5}
              value={toBeImplemented}
              onChange={handleChangeComments}
            />
            <FormInput
              style={{ marginBottom: "1rem" }}
              label={t("progressNoteForm.today")}
              fullWidth
              multiline
              rows={5}
              value={toBeImplemented}
              onChange={handleChangeComments}
            />
          </AccordionDetails>
        </Accordion>
        <Accordion
          className="data-accordion"
          expanded={expandedPanels.includes("panel-healthRisk")}
          onChange={handlePanelChange("panel-healthRisk")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel-healthRisk-content"
            id="health-risk-header"
          >
            {t("progressNoteForm.healthRisk")}
          </AccordionSummary>
          <AccordionDetails>
            <Caption>{toBeImplemented}</Caption>
          </AccordionDetails>
        </Accordion>
        <Accordion
          className="data-accordion"
          expanded={expandedPanels.includes("panel-preventiveMedicine")}
          onChange={handlePanelChange("panel-preventiveMedicine")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel-preventiveMedicine-content"
            id="preventine-medicine-header"
          >
            {t("progressNoteForm.preventineMedicine")}
          </AccordionSummary>
          <AccordionDetails>
            <Caption>{toBeImplemented}</Caption>
          </AccordionDetails>
        </Accordion>
        <Accordion
          className="data-accordion"
          expanded={expandedPanels.includes("panel-aditionalNotes")}
          onChange={handlePanelChange("panel-aditionalNotes")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel-aditionalNotes-content"
            id="aditional-notes-header"
          >
            {t("progressNoteForm.aditionalNotes")}
          </AccordionSummary>
          <AccordionDetails>
            <Caption>{toBeImplemented}</Caption>
          </AccordionDetails>
        </Accordion> */}
        <Box textAlign={"end"} position="fixed" bottom="2rem" right="10rem" zIndex={1000}>
          {appointmentId && chiefComplaints && hpiText && !readOnlyForm && !isPatient && (
            <PermissionWrapper
              requiredPermissions={[PermissionsUser[EntityPermissions.PROGRESS_NOTE].CREATE]}
            >
              <ButtonDialog
                onAcceptHandle={() => {
                  saveProgressNote();
                }}
                color
                name="ProgressNoteCreate"
                buttonText={t("progressNoteForm.btnDialogProgressText")}
                title={t("progressNoteForm.btnDialogProgressTitle")}
                description={t("progressNoteForm.btnDialogProgressDescription")}
                labelledbyID={"ProgressNoteCreate"}
                describedbyID={"ProgressNoteDescriptionId"}
              />
            </PermissionWrapper>
          )}
          {appointmentId && !isPatient && (
            (medicationText && medicationText.trim() !== originalMedicationText.trim()) ||
            (labText && labText.trim() !== originalLabText.trim()) ||
            (imagingText && imagingText.trim() !== originalImagingText.trim())
          ) && !preview && (
              <PermissionWrapper
                requiredPermissions={[PermissionsUser[EntityPermissions.PRESCRIPTIONS].CREATE]}
              >
                <Button
                  variant="contained"
                  color="success"
                  onClick={() => saveProgressNote()}
                  style={{ marginRight: 8 }}
                >
                  {t("progressNoteForm.savePrescriptions")}
                </Button>
              </PermissionWrapper>
            )}
        </Box>
      </Box>
      <Dialog
        open={openPrescriptionModal}
        onClose={() => setOpenPrescriptionModal(false)}
        aria-labelledby="prescription-dialog-title"
        aria-describedby="prescription-dialog-description"
      >
        <DialogTitle id="prescription-dialog-title">
          {t("progressNoteForm.printPrescription")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="prescription-dialog-description">
            <PrescriptionTemplate
              appointmentId={appointmentModel.id}
              printbtn={!!(medicationText || labText || imagingText)}
              medicationText={medicationText}
              labText={labText}
              imagingText={imagingText}
              notesMedicineText={notesMedicine}
              notesLabText={notesLab}
              notesImagingText={notesImaging}
            />
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPrescriptionModal(false)} color="secondary">
            {t("progressNoteForm.closed")}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="dialog-title"
        aria-describedby="dialog-description"
      >
        <DialogTitle id="dialog-title">{t("progressNoteForm.printProgressNote")}</DialogTitle>
        <DialogContent>
          <DialogContentText id="dialog-description">
            <HistoryProgressNoteCard
              appointmentId={appointmentModel.id}
              sectionsToPrint={sectionsToPrint}
              printbtn={false}
            />
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="secondary">
            {t("progressNoteForm.closed")}
          </Button>
          <Box ml={2} textAlign={"right"}>
            <ButtonDialog
              color={true}
              startIcon={true}
              disabled={false}
              onAcceptHandle={(e) => {
                handleListPrint(e);
              }}
              buttonText={t("progressNoteForm.btnDialogPrint")}
              title={t("progressNoteForm.btnDialogTitle")}
              description={accordionsList}
              labelledbyID={t("progressNoteForm.btnDialogPrint")}
              describedbyID={"PrintDescription"}
            />
          </Box>
        </DialogActions>
      </Dialog>
      {!isPatient && (
        <Dialog
          open={isBillingModalOpen}
          onClose={handleCloseBillingModal}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              minWidth: "80vw",
              minHeight: "70vh",
            },
          }}
        >
          <DialogTitle>
            <Stack direction="row" alignItems="center" gap={1}>
              <ReceiptIcon />
              <Typography variant="h5">
                {t("billingEncounterForm.title")}
              </Typography>
            </Stack>
          </DialogTitle>
          <DialogContent>
            {((appointmentId || appointmentIdFromState)) && appointmentModel.patient && (
              <AppointmentBillingEncountersForm
                appointmentId={appointmentId ?? appointmentIdFromState}
                patientId={appointmentModel.patient.id}
                onCloseAction={handleCloseBillingModal}
                insurance={Boolean(appointmentModel.patientInsurance)}
                nonBillable={Boolean(appointmentModel.nonBillable)}
              />
            )}
          </DialogContent>
        </Dialog>
      )}
      <Dialog
        open={openPrescriptionVersionModal}
        onClose={handleCancelPrescriptionVersion}
        aria-labelledby="prescription-version-dialog-title"
        aria-describedby="prescription-version-dialog-description"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="prescription-version-dialog-title">
          {t("progressNoteForm.confirmPrescriptionChange")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="prescription-version-dialog-description" sx={{ mb: 2 }}>
            {t("progressNoteForm.confirmPrescriptionChangeMessage")}
          </DialogContentText>
          <FormInput
            label={t("progressNoteForm.reasonForChange")}
            placeholder={t("progressNoteForm.reasonForChangePlaceholder")}
            fullWidth
            multiline
            rows={4}
            value={reasonForChange}
            onChange={(e) => setReasonForChange(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelPrescriptionVersion} color="secondary">
            {t("progressNoteForm.cancel")}
          </Button>
          <Button onClick={handleSavePrescriptionVersions} variant="contained" color="primary">
            {t("progressNoteForm.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
      <PrescriptionHistoryModal
        open={openPrescriptionHistoryModal}
        onClose={handleClosePrescriptionHistory}
        appointmentId={appointmentId || appointmentIdFromState}
      />
      {/* Time Warning Modal */}
      <Dialog
        open={showTimeWarningModal}
        onClose={() => { }} // Prevent closing by clicking outside
        aria-labelledby="time-warning-dialog-title"
        aria-describedby="time-warning-dialog-description"
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="time-warning-dialog-title">
          {t("progressNoteForm.timeWarningTitle")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="time-warning-dialog-description">
            {t("progressNoteForm.timeWarningMessage")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleStopWorking}
            color="secondary"
          >
            {t("progressNoteForm.no")}
          </Button>
          <Button
            onClick={handleContinueWorking}
            variant="contained"
            color="primary"
            autoFocus
          >
            {t("progressNoteForm.yes")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProgressNoteForm;
